import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled from 'styled-components';

import * as merch from '@webapp/sdk/merch';

import User from '@common/types/user/user';

import AppState from '@webapp/store/state';
import { getFullName } from '@webapp/store/user/selectors';
import { CloseMenu } from '@webapp/store/ui/action-creators';

import { getMainDomain } from '@webapp/utils/get-artist-site-url';

// import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Grid from '@components/basics/grid';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import SquareImg from '@components/basics/artwork-img';
import UserAvatarInfo from '@components/basics/user-avatar-info';

const mockupMerchImg =
  'https://images-api.printify.com/mockup/63370e058f8fb4121e4c67ef/22073/32/unisex-ultra-cotton-tee.jpg';

interface MerchMenuSuccessPageProps extends Partial<User> {
  className?: string;
  artistId?: string;
  productId: string;
  orderId?: string;
  isMobile?: boolean;
  closeMenu: () => CloseMenu;
}

const mapStateToProps = (state: AppState) => {
  const subdomain =
    state.subdomain.currentSubdomain?.subdomain ||
    state.artist.active?.shortId ||
    '';
  return {
    isMobile: state.ui.mobile,
    baseUrl: `https://${subdomain}.${getMainDomain()}/`,
    artistId: state.artist.active?.id as string,
    avatar: state.user.profile?.avatar,
    fullName: getFullName(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

const MerchMenuSuccess = (props: MerchMenuSuccessPageProps) => {
  const [product, setProduct] = React.useState<
    | {
        merchName: string | undefined;
        merchImg: string;
      }
    | undefined
  >();
  const { artistId } = props;

  React.useEffect(() => {
    let isRendered = true;
    const loadProduct = async (artistId: string, productId) => {
      const merchProduct = await merch.getProduct(artistId, productId);
      if (isRendered) {
        setProduct({
          merchName: merchProduct.title,
          merchImg:
            merchProduct?.sizes?.[0]?.images?.[0]?.src || mockupMerchImg,
        });
      }
    };

    if (artistId && props.productId) {
      loadProduct(artistId, props.productId);
    }

    return () => {
      isRendered = false;
    };
  }, [props.productId, artistId, props.orderId]);

  // TODO when we have order, show sample image

  return (
    <NewMenu
      className={props.className}
      menuId="MERCH_SUCCESS_MENU"
      centeredPadding={!props.isMobile}
      noButton
      topHeader={
        props.isMobile &&
        props.avatar && (
          <UserAvatarInfo src={props.avatar} name={props.fullName!} />
        )
      }
      header={
        !props.isMobile &&
        props.avatar && (
          <UserAvatarInfo src={props.avatar} name={props.fullName!} />
        )
      }
      footer={
        <Button fullWidth onClick={props.closeMenu}>
          <Translate id="COMMON.DONE" />
        </Button>
      }
    >
      <Grid>
        {!!product?.merchImg && (
          <WrapperImg className="mb-30">
            <SquareImg className="artwork" srcImg={product.merchImg} />
          </WrapperImg>
        )}
        {!!product?.merchName && (
          <InfoWrapper>
            <Typography
              className="mb-20"
              type="subheader"
              customFont="semi-bold"
            >
              <Translate
                id={
                  !!props.orderId
                    ? 'MERCH.ORDER_COMPLETED.TITLE.SAMPLE'
                    : 'MERCH.ORDER_COMPLETED.TITLE'
                }
                data={{ merchName: product.merchName }}
              />
            </Typography>
            <Text>
              <Translate
                id={
                  !!props.orderId
                    ? 'MERCH.ORDER_COMPLETED.SUBTITLE.SAMPLE'
                    : 'MERCH.ORDER_COMPLETED.SUBTITLE'
                }
                data={{ merchName: product.merchName }}
              />
            </Text>
          </InfoWrapper>
        )}
      </Grid>
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchMenuSuccess);

const WrapperImg = styled.div`
  width: 144px;
  height: 144px;
  aspect-ratio: 1/1;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.border};

  .artwork {
    width: 144px;
    height: 144px;
    margin: 0 auto;
    display: block;
    aspect-ratio: 1/1;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin: 0;
    }
  }
`;

const Text = styled(Typography)`
  color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
`;

const InfoWrapper = styled.div`
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    display: flex;
    flex-direction: column;
  }
`;
